import { IconArrowRight } from "@hawkins/assets";
import { ResultOf } from "gql.tada";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SolutionsChapterFragment } from "@queries/entries/SolutionsSectionById";
import kebabCase from "lodash/kebabCase";
import { useNavigate } from "react-router";
import { useLenis } from "lenis/react";
import cx from "classnames";

export default function SolutionsMobileMenu({
  chapters,
  activeChapterIndex,
  setIsExpanded,
  pageTitle,
}: {
  chapters: ResultOf<typeof SolutionsChapterFragment>[];
  activeChapterIndex: number;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  pageTitle: string;
}) {
  const navigation = useNavigate();
  const lenis = useLenis();

  const menuRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [clipPathValue, setClipPathValue] = useState(
    "inset(0 100% 0 0 round 8px)"
  );
  const [dotTop, setDotTop] = useState(0);

  const updateClipPath = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (menuRef.current) {
      const { offsetTop, offsetHeight } = event.currentTarget;
      const menuHeight = menuRef.current.offsetHeight;
      const padding = 6;

      const clipTop = ((offsetTop - padding) / menuHeight) * 100;
      const clipBottom =
        ((offsetTop + offsetHeight + padding) / menuHeight) * 100;

      setClipPathValue(`inset(${clipTop}% 0 ${100 - clipBottom}% 0 round 8px)`);
    }
  };

  const resetClipPath = useCallback(() => {
    if (isHovering) return;

    if (menuRef.current) {
      const activeChapter = chapters[activeChapterIndex];
      if (!activeChapter) return;

      const chapterTitle = kebabCase(
        activeChapter.menuLabel ?? activeChapter.eyebrow ?? ""
      );
      const activeItem = document.querySelector(
        `[data-chapter="${chapterTitle}"]`
      );

      if (activeItem) {
        updateClipPath({
          currentTarget: activeItem,
        } as React.MouseEvent<HTMLAnchorElement>);
      }
    }
  }, [activeChapterIndex, chapters, isHovering]);

  const updateDotTop = useCallback(() => {
    if (menuRef.current) {
      const activeChapter = chapters[activeChapterIndex];
      if (!activeChapter) return;
      const slug = kebabCase(
        activeChapter.menuLabel ?? activeChapter.eyebrow ?? ""
      );
      const el = document.querySelector(
        `[data-chapter="${slug}"]`
      ) as HTMLAnchorElement;
      if (el) {
        const { offsetTop, offsetHeight } = el;
        const padding = 6;

        // Calculate the top position of the dot to the center of the active chapter
        const newDotTop = offsetTop + offsetHeight / 2 - padding;

        setDotTop(newDotTop);
      }
    }
  }, [menuRef, activeChapterIndex, chapters]);

  useEffect(() => {
    updateDotTop();
    resetClipPath();
  }, [activeChapterIndex, resetClipPath, updateDotTop]);

  if (!chapters?.length) return null;

  const renderChapters = () => {
    return chapters.map(({ menuLabel, eyebrow, _id }, ix) => {
      const chapterTitle = menuLabel ?? eyebrow ?? "";
      const slug = kebabCase(chapterTitle);
      const active = activeChapterIndex === ix;

      return (
        // eslint-disable-next-line react/forbid-elements
        <a
          href={`#${slug}`}
          key={_id}
          data-chapter={slug}
          className={cx(
            "relative flex justify-between text-body-small py-[14px] rounded-[5px] items-center gap-4 text-center px-3",
            "dsk:py-2 dsk:text-left dsk:rounded-lg",
            "group hover:text-brand-cream hover:transition-colors hover:duration-half",
            active &&
              "group-hover/menu:text-brand-cream group-hover/items:[&:not(:hover)]:!text-brand-black"
          )}
          onMouseEnter={(e) => updateClipPath(e)}
          onClick={(e) => {
            e.preventDefault();
            navigation(`#${slug}`, {
              replace: true,
            });
            setIsExpanded(false);
            const chapterEl = document.getElementById(slug);
            if (chapterEl) {
              lenis?.scrollTo(chapterEl);
            }
          }}
        >
          <span className="flex-grow">{chapterTitle}</span>
          <IconArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity duration-half" />
        </a>
      );
    });
  };

  return (
    <menu
      ref={menuRef}
      className="col-span-5 hidden dsk:flex sticky top-20 h-min bg-neutral-grey/50 backdrop-blur-[25px] rounded-lg text-brand-cream overflow-hidden py-[25px] px-3 flex-col hover:bg-brand-cream hover:text-brand-black transition-colors duration-half group/menu"
    >
      <p className="text-body-small pb-4" onMouseEnter={resetClipPath}>
        {pageTitle}
      </p>
      <div
        aria-hidden
        className="absolute top-0 left-3 w-[calc(100%-24px)] h-full bg-palette-current-background transition-[clip-path] duration-half px-3"
        style={{
          clipPath: clipPathValue,
        }}
      />
      <div
        ref={menuRef}
        onMouseLeave={() => {
          setIsHovering(false);
          resetClipPath();
        }}
        className="group/items ml-6"
        onMouseEnter={() => setIsHovering(true)}
      >
        <span
          className={cx(
            "bg-palette-current-foreground rounded-full w-2 h-2 opacity-100 absolute left-6 transition-all duration-half"
          )}
          style={{
            top: dotTop,
          }}
        />
        {renderChapters()}
      </div>
    </menu>
  );
}
